import React from 'react';
import type { BaseSyntheticEvent, MouseEvent, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { TextField } from '@backyard-ui/core';
import { useMediaQuery } from '@backyard-ui/hooks';

import { createAutocomplete } from '@algolia/autocomplete-core';
import type { AutocompleteState } from '@algolia/autocomplete-core';
import {
  getAlgoliaResults,
  RequestParams,
} from '@algolia/autocomplete-preset-algolia';
import { useSearchBox } from 'react-instantsearch';

import QuerySuggestions from './Results/QuerySuggestions';
import ProductList from './Results/ProductList';
import RecentsSearch from './Results/RecentsSearch';
import ServiceList from './Results/ServiceList';

import { plugins } from '../Searchbar.plugins';
import type { PluginsOptions } from '../Searchbar.plugins';

import { sendGTMEvent } from '../../../../utils/sendGTMEvent';
import { useSearchParams } from '../../../../hooks/use-search-params';
import { useHeaderStore } from '../../../../Header.store';
import type { AutocompleteItem } from '../Searchbar.types';

import styles from './ServicesSearchbar.styles';
import { PRODUCTS_AND_SERVICES_PLACEHOLDER } from '../constants';
import { useSearchTextField } from '../hooks';
import { getAutocompleteProps } from '../Searchbar.utilts';

export interface SearchbarProps {
  algolia: PluginsOptions & RequestParams<AutocompleteItem>;
  /**
   * Maximum width for the search results container.
   * This prop was added because Kameleon and Ameno projects have different container sizes.
   * @default '7xl'
   */
  maxWidth?: '6xl' | '7xl';
}

function ServicesSearchbar(props: SearchbarProps) {
  const { algolia, maxWidth = '7xl' } = props;

  const { routing, region } = useHeaderStore((state) => ({
    routing: state.routing,
    region: state.locationContext.region,
  }));
  const { refine } = useSearchBox();
  const query = useSearchParams();

  const [autocompleteState, setAutocompleteState] = React.useState<
    AutocompleteState<AutocompleteItem>
  >({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: '',
    activeItemId: null,
    status: 'idle',
  });

  const inputRef = React.useRef<HTMLInputElement>(null);
  const formRef = React.useRef<HTMLFormElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [panelPosition, setPanelPosition] = React.useState({ top: 0 });
  const [isMounted, setIsMounted] = React.useState(false);

  const isDesktop = useMediaQuery('lg');

  // Prevent hydration errors by only rendering portals after mount
  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  const { recents, suggestions, insights, redirect } = React.useMemo(() => {
    return plugins(
      {
        indexName: algolia.indexName,
        insightsClient: algolia.insightsClient,
        searchClient: algolia.searchClient,
      },
      region,
    );
  }, [algolia.indexName, algolia.insightsClient, algolia.searchClient, region]);

  const autocomplete = React.useMemo(() => {
    return createAutocomplete<
      AutocompleteItem,
      BaseSyntheticEvent,
      MouseEvent,
      KeyboardEvent
    >({
      placeholder: PRODUCTS_AND_SERVICES_PLACEHOLDER,
      insights: true,
      openOnFocus: true,
      plugins: region ? [recents, suggestions, insights, redirect] : [],
      initialState: {
        query: decodeURIComponent(query.term || ''),
      },
      onReset() {
        setAutocompleteState((prev) => ({
          ...prev,
          query: '',
        }));
      },
      onStateChange({ prevState, state }) {
        if (prevState.query !== state.query) {
          setAutocompleteState((prev) => ({
            ...prev,
            query: state.query,
            isOpen: state.isOpen && !!region,
          }));
        } else {
          setAutocompleteState({
            ...state,
            isOpen: state.isOpen && !!region,
          });
        }
      },
      onSubmit({ state }) {
        if (!state.query) {
          return;
        }

        sendGTMEvent({
          value: {
            search: {
              term: state.query,
              type: 'default',
            },
          },
        });

        if (routing) {
          window.location.href = `/search?term=${state.query}&searchTerm=${state.query}&searchType=default`;
        } else {
          refine(state.query);
        }
      },
      ...(region && {
        getSources: () => {
          return [
            {
              sourceId: 'products',
              getItemInputValue({ item }) {
                return item.query;
              },
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient: algolia.searchClient,
                  queries: algolia.queries.map((q) => ({
                    ...q,
                    query,
                    indexName:
                      algolia.retailMediaIndexName || algolia.indexName,
                    params: {
                      ...q.params,
                      hitsPerPage: q.params?.hitsPerPage ?? 4,
                      clickAnalytics: q.params?.clickAnalytics ?? true,
                      filters:
                        q.params?.filters ??
                        `regionalAttributes.${region}.promotionalPrice>0 AND regionalAttributes.${region}.available=1`,
                    },
                  })),
                });
              },
              onSelect({ state }) {
                sendGTMEvent({
                  value: {
                    search: {
                      term:
                        state.query || inputRef?.current?.value || 'sem-termo',
                      type: 'recommendedProduct',
                    },
                  },
                });
              },
            },
            {
              sourceId: 'productServices',
              getItemInputValue({ item }) {
                return item.query;
              },
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient: algolia.searchClient,
                  queries: algolia.queries.map((q) => ({
                    ...q,
                    query,
                    indexName: algolia.productServicesIndexName || '',
                    params: {
                      ...q.params,
                      hitsPerPage: q.params?.hitsPerPage ?? 4,
                      clickAnalytics: q.params?.clickAnalytics ?? true,
                      filters:
                        q.params?.filters ??
                        `regionalAttributes.${region}.promotionalPrice>0 AND regionalAttributes.${region}.available=1`,
                    },
                  })),
                });
              },
              onSelect({ state }) {
                sendGTMEvent({
                  value: {
                    search: {
                      term:
                        state.query || inputRef?.current?.value || 'sem-termo',
                      type: 'recommendedProduct',
                    },
                  },
                });
              },
            },
          ];
        },
      }),
    });
  }, [
    recents,
    suggestions,
    insights,
    redirect,
    routing,
    query,
    refine,
    algolia,
    region,
  ]);

  React.useEffect(() => {
    if (!(formRef.current && panelRef.current && inputRef.current)) {
      return;
    }

    // On mobile, don't add touch/mouse listeners to allow free scrolling
    if (!isDesktop) {
      return;
    }

    const { onTouchStart, onTouchMove, onMouseDown } =
      autocomplete.getEnvironmentProps({
        formElement: formRef.current,
        panelElement: panelRef.current,
        inputElement: inputRef.current,
      });

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !formRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        autocomplete.setIsOpen(false);
      }
    };

    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('pointerdown', onPointerDown);

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [autocomplete, autocompleteState.isOpen, isDesktop]);

  React.useEffect(() => {
    if (autocompleteState.isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPanelPosition({
        top: rect.bottom + window.scrollY,
      });
    }
  }, [autocompleteState.isOpen]);

  React.useEffect(() => {
    if (!autocompleteState.isOpen || !isDesktop) return;

    const handleScroll = () => {
      autocomplete.setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [autocompleteState.isOpen, autocomplete, isDesktop]);

  const AUTOCOMPLETE_ID = '@autocomplete-quicksearch-input';
  const AUTOCOMPLETE_LABEL = '@autocomplete-quicksearch-label';

  const autocompleteProps = getAutocompleteProps(
    autocomplete,
    inputRef,
    AUTOCOMPLETE_ID,
  );

  const hasRecentSearchQuery = Boolean(
    recents.data?.getAlgoliaSearchParams().facetFilters?.length,
  );

  const hasServices = React.useMemo(() => {
    const servicesCollection = autocompleteState.collections.find(
      (item) => item.source.sourceId === 'productServices',
    );
    return Boolean(servicesCollection && servicesCollection.items.length > 0);
  }, [autocompleteState.collections]);

  const searchTextField = useSearchTextField({
    autocomplete,
    autocompleteState,
    setAutocompleteState,
    inputRef,
  });

  return (
    <>
      {/* Overlay rendered in body via portal - desktop only */}
      {isMounted &&
        isDesktop &&
        createPortal(
          <div
            className={styles({ isOpen: autocompleteState.isOpen }).overlay()}
            style={{
              top: panelPosition.top > 0 ? `${panelPosition.top + 8}px` : '0',
            }}
          />,
          document.body,
        )}

      {/* Search TextField */}
      <div
        className={styles({ isOpen: autocompleteState.isOpen }).base()}
        {...autocompleteProps.root}
      >
        <form
          className={styles().form()}
          ref={formRef}
          {...autocompleteProps.form}
        >
          <TextField.Root>
            {searchTextField.leadingSlot}
            {searchTextField.searchInput}
            {searchTextField.trailingSlot}
          </TextField.Root>
        </form>
      </div>

      {/* Autocomplete Results Panel - rendered in body via portal */}
      {isMounted &&
        createPortal(
          <div
            className={styles({ isOpen: autocompleteState.isOpen }).panel()}
            aria-labelledby={AUTOCOMPLETE_LABEL}
            ref={panelRef}
            style={{ top: `${panelPosition.top}px` }}
            {...autocompleteProps.panel}
          >
            {autocompleteState.isOpen && (
              <ul
                className={styles({
                  isOpen: autocompleteState.isOpen,
                  areas: hasRecentSearchQuery,
                  hasServices,
                  maxWidth,
                }).grid()}
              >
                {autocompleteState.collections.map((item) => {
                  if (
                    item.source.sourceId === 'recentSearchesPlugin' &&
                    hasRecentSearchQuery
                  ) {
                    return (
                      <RecentsSearch
                        key={item.source.sourceId}
                        refine={refine}
                        {...item}
                        {...autocomplete}
                        {...recents}
                      />
                    );
                  }

                  if (item.source.sourceId === 'querySuggestionsPlugin') {
                    return (
                      //@ts-ignore
                      <QuerySuggestions
                        key={item.source.sourceId}
                        query={autocompleteState.query}
                        refine={refine}
                        {...item}
                        {...autocomplete}
                      />
                    );
                  }

                  if (item.source.sourceId === 'products') {
                    return (
                      <ProductList
                        key={item.source.sourceId}
                        {...item}
                        {...autocomplete}
                      />
                    );
                  }

                  if (
                    item.source.sourceId === 'productServices' &&
                    item.items.length > 0
                  ) {
                    return (
                      <ServiceList
                        key={item.source.sourceId}
                        {...item}
                        {...autocomplete}
                      />
                    );
                  }

                  return null;
                })}
              </ul>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

export default React.memo(ServicesSearchbar);
